import { Prisma, prisma, AdjustmentRuleType, AdjustableType } from "client";
import {
  calculateTaxBySubtotal,
  dineroSubUnit,
  numberToCurrency,
} from "@sajilni/money/helpers";

interface TotalCharge {
  totalPercentage: number;
  totalFlat: number;
  totalTax?: number;
  taxable?: boolean;
}

interface CalculationType {
  subTotal: number;
  totalDiscount: number;
  totalCharge?: TotalCharge;
}

let CalculationDefaultValue: CalculationType = {
  subTotal: 0,
  totalDiscount: 0,
  totalCharge: {
    totalPercentage: 0,
    totalFlat: 0,
    totalTax: 0,
    taxable: false,
  },
};

export const getAppliedAdjustment = async (
  eventId: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) => {
  try {
    const skip = (page - 1) * pageSize;
    const take = pageSize;
    let whereClause: Prisma.AppliedAdjustmentWhereInput = {};

    if (search) {
    }
    return (
      await prisma.appliedAdjustment.findMany({
        where: whereClause,
        orderBy: {
          id: "desc",
        },
        skip: skip,
        take: take,
      }),
      prisma.appliedAdjustment.count({
        where: whereClause,
      })
    );
  } catch (error) {
    console.error("Error fetching applied adjustment by ID:", error);
    throw error;
  }
};

export const deleteAppliedAdjustmentOnRefresh = async (
  adjustableId: string,
  eventId: string
) => {
  try {
    const getData = await prisma.appliedAdjustment.findMany({
      where: {
        adjustableId: adjustableId,
        eventId: eventId,
      },
    });

    for await (const item of getData) {
      await prisma.appliedAdjustment.delete({
        where: {
          id: item.id,
        },
      });
    }

    return getData;
  } catch (error) {
    console.error("Error creating applied adjustment:", error);
    throw error;
  }
};

export const deleteAppliedAdjustmentCharge = async (
  adjustableId: string,
  eventId: string
) => {
  try {
    const getData = await prisma.appliedAdjustment.findMany({
      where: {
        adjustableId: adjustableId,
        eventId: eventId,
        adjustableType: AdjustableType.PAYMENT,
      },
    });
    for await (const item of getData) {
      await prisma.appliedAdjustment.delete({
        where: {
          id: item.id,
        },
      });
    }

    return null;
  } catch (error) {
    console.error("Error creating applied adjustment:", error);
    throw error;
  }
};

export const createAppliedAdjustment = async (data: any) => {
  try {
    return await prisma.appliedAdjustment.create({
      data: {
        ...data,
      },
    });
  } catch (error) {
    console.error("Error creating applied adjustment:", error);
    throw error;
  }
};

export const deleteAppliedAdjustmentById = async (id: string) => {
  try {
    const appliedAdjustment = await prisma.appliedAdjustment.delete({
      where: {
        id: id,
      },
    });

    return appliedAdjustment;
  } catch (error) {
    console.error("Error fetching applied adjustment by ID:", error);
    throw error;
  }
};

const getAppliedAdjustmentData = async (adjustableId: string) => {
  const appliedAdjustment = await prisma.appliedAdjustment.findMany({
    where: {
      adjustableId: adjustableId,
    },
    include: {
      adjustmentRule: {
        include: {
          discountAdjustments: true,
          chargeAdjustments: true,
        },
      },
    },
  });

  return appliedAdjustment;
};

export const getAppliedAdjustmentById = async (adjustableId: string) => {
  try {
    const appliedAdjustment = await getAppliedAdjustmentData(adjustableId);

    // calculate discount on Cart
    let discountOrderData = appliedAdjustment.filter(
      (item) =>
        item.adjustmentRule?.type === AdjustmentRuleType.DISCOUNT &&
        item.adjustmentRule?.ticketIds?.length == 0
    );
    let orderDiscountData: CalculationType = CalculationDefaultValue;
    orderDiscountData = await discountOrderCalculation(
      discountOrderData,
      adjustableId
    );
    // calculate discount per OrderItem
    let discountOrderItemData = appliedAdjustment.filter(
      (item) =>
        item.adjustmentRule?.type === AdjustmentRuleType.DISCOUNT &&
        item.adjustmentRule?.ticketIds?.length > 0
    );
    let orderItemDiscountData: CalculationType = CalculationDefaultValue;
    if (discountOrderItemData.length > 0) {
      orderItemDiscountData = await discountOrderItemCalculation(
        discountOrderItemData,
        adjustableId
      );
    }

    let chargeData = appliedAdjustment.filter(
      (item) =>
        item.adjustmentRule?.type === AdjustmentRuleType.CHARGE &&
        item.adjustmentRule?.ticketIds?.length == 0 &&
        item.adjustmentRule?.paymentMethods?.length == 0
    );
    let orderCharge: CalculationType = CalculationDefaultValue;
    if (chargeData.length > 0) {
      orderCharge = await chargeOrderCalculation(chargeData, adjustableId);
    }

    let chargeOrderItemData = appliedAdjustment.filter(
      (item) =>
        item.adjustmentRule?.type === AdjustmentRuleType.CHARGE &&
        item.adjustmentRule?.ticketIds?.length > 0 &&
        item.adjustmentRule?.paymentMethods?.length == 0
    );
    let orderItemCharge: number = 0;
    if (chargeOrderItemData.length > 0) {
      orderItemCharge = await chargeOrderItemCalculation(
        chargeOrderItemData,
        adjustableId
      );
    }

    const chargePaymentData = appliedAdjustment.filter(
      (item) =>
        item.adjustmentRule?.type === AdjustmentRuleType.CHARGE &&
        item.adjustmentRule?.paymentMethods?.length > 0
    );

    let orderPaymentCharge: TotalCharge = { totalPercentage: 0, totalFlat: 0 };
    if (chargePaymentData.length > 0) {
      orderPaymentCharge = await chargePaymentCalculation(
        chargePaymentData,
        adjustableId
      );
    }

    let result = await calculationTotal(
      orderDiscountData,
      orderItemDiscountData,
      orderCharge,
      orderItemCharge,
      orderPaymentCharge,
      adjustableId
    );

    return { data: appliedAdjustment, calculation: result };
  } catch (error) {
    console.error("Error fetching applied adjustment by ID:", error);
    throw error;
  }
};

const discountOrderItemCalculation = async (data: any, orderId: string) => {
  // Discount OrderItem Level
  const order = await prisma.order.findUnique({ where: { id: orderId } });
  let subUnit = dineroSubUnit({
    currency: order?.currency as string,
  }) as number;
  let dataClean = await cleanPerItem(order?.lineItems);
  let dataWithDiscountAmountOrderItem = await addDiscountToOrderItem(
    data,
    dataClean,
    subUnit
  );
  return dataWithDiscountAmountOrderItem;
};

const discountOrderCalculation = async (data: any, orderId: string) => {
  // Discount Order Level
  const order = await prisma.order.findUnique({ where: { id: orderId } });
  let dataClean = await cleanPerItem(order?.lineItems);
  let subUnit: any = dineroSubUnit({ currency: order?.currency as string });
  let dataWithDiscountAmountOrder = await addDiscountToOrder(
    data,
    dataClean,
    subUnit
  );
  return dataWithDiscountAmountOrder;
};

const chargeOrderCalculation = async (data: any, orderId: string) => {
  // Charge Order Level
  const order = await prisma.order.findUnique({ where: { id: orderId } });
  let dataClean = await cleanPerItem(order?.lineItems);
  let subUnit: any = dineroSubUnit({ currency: order?.currency as string });
  let dataWithChargeAmountOrder = await chargeOrder(data, dataClean, subUnit);
  return dataWithChargeAmountOrder;
};

const chargeOrderItemCalculation = async (data: any, orderId: string) => {
  // Charge OrderItem Level
  const order = await prisma.order.findUnique({ where: { id: orderId } });
  let subUnit = dineroSubUnit({
    currency: order?.currency as string,
  }) as number;
  let taxRate = order?.taxRate as number;
  let dataClean = await cleanPerItem(order?.lineItems);
  let dataWithDiscountAmountOrderItem = await addChargeToOrderItem(
    data,
    dataClean,
    subUnit,
    taxRate
  );
  return dataWithDiscountAmountOrderItem;
};

const chargePaymentCalculation = async (
  data: any,
  orderId: string
): Promise<TotalCharge> => {
  const orderData = await getOrderDetails(orderId);
  if (!orderData) return { totalPercentage: 0, totalFlat: 0 };
  const taxRate = orderData?.taxRate / 100;
  const subUnit = orderData?.subUnit;
  let totalPercentage = 0;
  let totalFlat = 0;
  let totalTax = 0;

  data.forEach(async (item: any) => {
    const taxable = item?.adjustmentRule?.chargeAdjustments?.[0]?.taxable;
    const percentage = (item?.percentage || 0) / 100;
    const flatAmount = (item?.flatAmount || 0) * subUnit;
    let chargeAmount = percentage + (item?.flatAmount || 0);
    chargeAmount = chargeAmount * subUnit;
    let chargeAmountWithTax = 0;
    if (taxable) {
      chargeAmountWithTax = chargeAmount * taxRate;
    }
    totalTax += chargeAmountWithTax;
    totalPercentage += percentage;
    totalFlat += flatAmount;
  });

  return { totalPercentage, totalFlat, totalTax };
};

const calculationTotal = async (
  DiscountOrderData: CalculationType,
  DiscountOrderItemData: CalculationType,
  ChargeOrderData: CalculationType,
  ChargeOrderItemWithTax: number,
  orderPaymentChargeData: TotalCharge,
  orderId: string
) => {
  let subTotalOrder = DiscountOrderData.subTotal;
  let totalDiscountOrder = DiscountOrderData.totalDiscount;
  let totalChargePercentageOrder =
    ChargeOrderData.totalCharge?.totalPercentage ?? 0;
  let totalChargeFlatOrder = ChargeOrderData.totalCharge?.totalFlat ?? 0;
  let taxableChargeOrder = ChargeOrderData.totalCharge?.taxable ?? false;

  let totalDiscountOrderItem = DiscountOrderItemData.totalDiscount;
  let totalChargeOrderItemWithTax = ChargeOrderItemWithTax ?? 0;

  let subTotalAmount = subTotalOrder; // on Cart Level
  let totalDiscountAmount = totalDiscountOrder + totalDiscountOrderItem;

  let orderPaymentPercentage = orderPaymentChargeData?.totalPercentage ?? 0;
  let orderPaymentFlat = orderPaymentChargeData?.totalFlat ?? 0;
  let orderPaymenttotalChargeWithTax = orderPaymentChargeData?.totalTax ?? 0;

  let subTotalTaxableAmount = await calculateOrderTax(
    orderId,
    totalDiscountAmount
  );
  let totalTax = await calculateTax(subTotalTaxableAmount, orderId);

  const subTotalAfterDiscount = subTotalAmount - totalDiscountAmount;

  // Order Level
  const totalChargeOrderPercentage =
    subTotalAfterDiscount * totalChargePercentageOrder;
  const chargeOrder = totalChargeOrderPercentage + totalChargeFlatOrder;
  const totalChargeOrderWithTax = taxableChargeOrder
    ? await calculateTax(chargeOrder, orderId)
    : 0;
  // Charge with tax on Order Level.
  const totalChargeOrder = chargeOrder + totalChargeOrderWithTax;

  // Order Item Level
  const totalChargeOrderItem = totalChargeOrderItemWithTax;
  console.log("totalChargeOrderItem", totalChargeOrderItem);
  // order level + order item
  const totalCharge = totalChargeOrder + totalChargeOrderItem;

  const orderTotal =
    subTotalAmount - totalDiscountAmount + totalTax + totalCharge;

  const paymentFees = orderTotal * orderPaymentPercentage + orderPaymentFlat;

  const paymentFeesTax = orderPaymenttotalChargeWithTax;

  const paymentFeesTotal = paymentFees + paymentFeesTax;

  const grandTotal = orderTotal + paymentFeesTotal; // + ChargeTax

  return {
    subTotal: grandTotal > 0 ? subTotalAmount : 0,
    totalTax: totalTax,
    totalDiscountAmount: totalDiscountAmount,
    totalCharge: totalCharge,
    paymentFees: paymentFeesTotal,
    grandTotal: grandTotal > 0 ? grandTotal : 0,
  };
};

const cleanPerItem = async (data: any) => {
  let dataClean: any[] = [];
  data?.forEach((item: any) => {
    delete item.description;
    delete item.name;
    delete item.image;
    dataClean.push(item);
  });

  return dataClean;
};

const addDiscountToOrderItem = async (
  data: any,
  processedItems: any,
  subUnit: number
) => {
  // Clone processedItems for modification
  let dataWithDiscountAmount: any[] = processedItems.map((item: any) => ({
    ...item,
    discountAmount: 0, // Initialize discountAmount to 0
  }));

  data?.forEach((discountRule: any) => {
    const ticketIds = discountRule?.adjustmentRule?.ticketIds || [];
    const percentage = (discountRule?.percentage || 0) / 100;
    const flatAmount = (discountRule?.flatAmount || 0) * subUnit;

    // Filter relevant order items
    const affectedItems = dataWithDiscountAmount.filter((orderItem: any) =>
      ticketIds.includes(orderItem.id)
    );

    affectedItems.forEach((orderItem: any) => {
      const percentageAmount = orderItem.price * percentage;
      const totalDiscount = percentageAmount + flatAmount;

      // Update the discountAmount
      orderItem.discountAmount += totalDiscount;
    });
  });

  let totalDiscount = await sumByKey(dataWithDiscountAmount, "discountAmount");

  return { subTotal: 0, totalDiscount: totalDiscount };
};

const addDiscountToOrder = async (
  data: any,
  processedItems: any,
  subUnit: number
) => {
  // Clone processedItems for modification
  let dataWithDiscountAmount: any[] = processedItems.map((item: any) => ({
    ...item,
    discountAmount: 0, // Initialize discountAmount to 0
  }));

  data?.forEach((discountRule: any) => {
    const percentage = (discountRule?.percentage ?? 0) / 100;
    const flatAmount = (discountRule?.flatAmount ?? 0) * subUnit;

    dataWithDiscountAmount.forEach((orderItem: any) => {
      const percentageAmount = orderItem.price * percentage;
      const totalDiscount = percentageAmount + flatAmount;

      // Update the discountAmount
      orderItem.discountAmount += totalDiscount;
    });
  });

  let subTotal = await sumByKey(dataWithDiscountAmount, "price");
  let totalDiscount = await sumByKey(dataWithDiscountAmount, "discountAmount");

  return { subTotal: subTotal, totalDiscount: totalDiscount };
};

const chargeOrder = async (data: any, processedItems: any, subUnit: number) => {
  // Clone processedItems for modification
  let dataWithChargeAmount: any[] = processedItems.map((item: any) => ({
    ...item,
    percentageAmount: 0, // Initialize percentageAmount to 0
    flatAmount: 0, // Initialize flatAmount to 0
    tax: 0,
  }));
  let taxable;
  data.forEach((chargeRule: any) => {
    const percentage = (chargeRule?.percentage ?? 0) / 100;
    const flatAmount = (chargeRule?.flatAmount ?? 0) * subUnit;
    taxable = chargeRule?.adjustmentRule?.chargeAdjustments?.[0]?.taxable;
    dataWithChargeAmount.forEach(async (orderItem: any) => {
      const percentageAmount = percentage;
      const totalFlatCharge = flatAmount;

      // Update
      orderItem.percentageAmount += percentageAmount;
      orderItem.flatAmount += totalFlatCharge;
    });
  });

  let totalCharge = await sumCharge(dataWithChargeAmount);
  totalCharge.taxable = taxable;

  console.log({ subTotal: 0, totalDiscount: 0, totalCharge: totalCharge });

  return { subTotal: 0, totalDiscount: 0, totalCharge: totalCharge };
};

const addChargeToOrderItem = async (
  data: any,
  processedItems: any,
  subUnit: number,
  taxRate: number
) => {
  let totalCharge = 0;
  data?.forEach((discountRule: any) => {
    const taxable =
      discountRule?.adjustmentRule?.chargeAdjustments?.[0]?.taxable;
    const ticketIds = discountRule?.adjustmentRule?.ticketIds || [];
    const percentage = (discountRule?.percentage || 0) / 100;
    const flatAmount = (discountRule?.flatAmount || 0) * subUnit;
    const rate = taxRate / 100;
    let taxPerItem = 0;
    // Filter relevant order items
    const affectedItems = processedItems.filter((orderItem: any) =>
      ticketIds.includes(orderItem.id)
    );
    affectedItems.forEach((orderItem: any) => {
      let percentageByPrice = orderItem?.price * percentage;
      let chargeAmount = percentageByPrice + flatAmount;
      if (taxable) {
        taxPerItem = chargeAmount * rate;
      }
      // Update
      totalCharge += chargeAmount + taxPerItem;
    });
  });

  return totalCharge;
};

const getOrderDetails = async (orderId: string) => {
  const order = await prisma.order.findUnique({ where: { id: orderId } });
  let dataClean = await cleanPerItem(order?.lineItems);
  let subUnit = (await dineroSubUnit({
    currency: order?.currency as string,
  })) as number;
  const taxRate = order?.taxRate as number;
  const currency = order?.currency as string;

  return {
    dataClean: dataClean,
    subUnit: subUnit,
    taxRate: taxRate,
    currency: currency,
  };
};

const sumByKey = async (data: any[], key: string): Promise<number> => {
  let total = 0;
  for (const item of data || []) {
    total += item[key] || 0;
  }
  return total;
};

const sumCharge = async (data: any[]) => {
  let totalPercentage = 0;
  let totalFlat = 0;
  for (const item of data || []) {
    totalPercentage += item.percentageAmount;
    totalFlat += item.flatAmount;
  }
  let result: TotalCharge = {
    totalPercentage,
    totalFlat,
  };
  return result;
};

const calculateTax = async (
  value: number,
  orderId: string
): Promise<number> => {
  const orderData = await getOrderDetails(orderId);

  if (!orderData) return 0;
  let totalTax = 0;
  const itemCharge = numberToCurrency(value, orderData.currency, 3);
  totalTax += await calculateTaxBySubtotal({
    subTotal: itemCharge,
    subUnit: orderData.subUnit,
    taxRate: orderData.taxRate,
    currency: orderData.currency,
  });
  return totalTax;
};

const calculateOrderTax = async (
  orderId: string,
  discountAmount: number
): Promise<number> => {
  const orderData = await getOrderDetails(orderId);
  if (!orderData) return 0;
  let totalTaxable = 0;
  let subTotal = 0;
  orderData?.dataClean?.forEach(async (item) => {
    if (item.taxable) {
      totalTaxable += item?.price;
    }
  });
  subTotal = totalTaxable - discountAmount;
  if (subTotal < 0) return 0;
  return subTotal;
};
